<template>
  <el-card shadow="never" v-loading="postsStore.loading" :body-style="{ padding: '14px' }">
    <template #header>
      <div class="table-header">
        <span class="table-title">📝 Посты</span>
        <span class="table-count">Показано {{ postsStore.filteredPosts.length }} постов</span>
      </div>
    </template>

    <!-- Пустое состояние -->
    <el-empty
      v-if="postsStore.filteredPosts.length === 0 && !postsStore.loading"
      description="Нет постов по выбранным фильтрам"
    >
      <template #image>
        <el-icon size="64" color="#dcdfe6"><Document /></el-icon>
      </template>
      <template #description>
        <div class="empty-desc">
          <p>Нет постов для отображения. Попробуйте изменить фильтры.</p>
          <el-button type="primary" size="small" @click="resetFilters">Сбросить фильтры</el-button>
        </div>
      </template>
    </el-empty>

    <!-- Таблица -->
    <el-table
      v-else
      :data="postsStore.filteredPosts"
      style="width: 100%"
      @row-click="(row: any) => $emit('openDetail', row.id)"
      highlight-current-row
      class="posts-table"
      size="large"
    >
      <!-- Чекбокс выбора -->
      <el-table-column v-if="authStore.canValidatePosts" width="50" align="center">
        <template #default="{ row }">
          <el-checkbox
            :model-value="postsStore.isSelected(row.id)"
            @change="() => postsStore.toggleSelection(row.id)"
            @click.stop
            size="large"
          />
        </template>
      </el-table-column>

      <!-- Содержание -->
      <el-table-column label="Содержание" min-width="320">
        <template #default="{ row }">
          <div class="content-cell">
            <div v-if="row.explanation" class="ai-reason">
              <span class="ai-badge">🤖</span>
              <MarkdownRenderer :text="truncate(row.explanation, 300)" />
            </div>
            <div v-else class="content-preview">
              <MarkdownRenderer :text="truncate(row.content, 300)" />
            </div>
          </div>
        </template>
      </el-table-column>

      <!-- Федеральный округ -->
      <el-table-column label="ФО" width="120" align="center">
        <template #default="{ row }">
          <el-tag
            v-if="row.federal_district"
            size="small"
            :type="districtType(row.federal_district)"
            effect="dark"
          >
            {{ row.federal_district }}
          </el-tag>
          <span v-else class="no-district">—</span>
        </template>
      </el-table-column>

      <!-- Дата -->
      <el-table-column label="Дата" width="150">
        <template #default="{ row }">
          <div class="date-cell">
            {{ formatDate(row.published_at || row.scraped_at) }}
          </div>
        </template>
      </el-table-column>

      <!-- Статус -->
      <el-table-column label="Статус" width="140" align="center">
        <template #default="{ row }">
          <el-tag size="small" :type="statusType(row.status)" effect="plain">
            {{ statusLabel(row.status) }}
          </el-tag>
        </template>
      </el-table-column>

      <!-- Действия -->
      <el-table-column v-if="authStore.canValidatePosts" label="Действия" width="170" align="center" fixed="right">
        <template #default="{ row }">
          <div class="action-buttons" @click.stop
>
            <el-tooltip content="Жалоба" placement="top">
              <el-button
                type="success"
                size="small"
                circle
                :icon="Check"
                @click="validate(row.id, 'COMPLAINT')"
              />
            </el-tooltip>
            <el-tooltip content="Ложное срабатывание" placement="top">
              <el-button
                type="danger"
                size="small"
                circle
                :icon="Close"
                @click="validate(row.id, 'FALSE_POS')"
              />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </el-card>
</template>

<script setup lang="ts">
import { Check, Close, Document } from '@element-plus/icons-vue'
import { usePostsStore } from '@/stores/posts'
import { useAuthStore } from '@/stores/auth'
import MarkdownRenderer from '@/components/MarkdownRenderer.vue'
import { formatAppDateTime } from '@/config/timezone'

const postsStore = usePostsStore()
const authStore = useAuthStore()

defineEmits<{
  (e: 'openDetail', postId: number): void
}>()

async function validate(postId: number, verdict: 'COMPLAINT' | 'FALSE_POS') {
  const ok = await postsStore.validatePost(postId, verdict)
  if (ok) {
    await postsStore.loadPosts(true)
  }
}

function resetFilters() {
  postsStore.clearFilters()
  postsStore.page = 0
  postsStore.loadPosts(true)
}

function truncate(text: string | null, max: number): string {
  if (!text) return ''
  return text.length > max ? text.slice(0, max) + '...' : text
}

function formatDate(dateStr: string | null): string {
  return formatAppDateTime(dateStr, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function statusType(status: string): '' | 'success' | 'warning' | 'info' | 'danger' {
  switch (status?.toLowerCase()) {
    case 'flagged': return 'warning'
    case 'validated': return 'success'
    case 'archived': return 'info'
    case 'new': return ''
    case 'in_report': return 'info'
    default: return 'info'
  }
}

function statusLabel(status: string): string {
  switch (status?.toLowerCase()) {
    case 'new': return 'Новый'
    case 'flagged': return 'На проверке'
    case 'validated': return 'Подтверждён'
    case 'in_report': return 'В отчёте'
    case 'archived': return 'Архив'
    default: return status
  }
}

function districtType(district: string): '' | 'success' | 'warning' | 'danger' | 'primary' | 'info' {
  switch (district) {
    case 'ЦФО': return 'danger'
    case 'СЗФО': return 'primary'
    case 'ЮФО': return 'warning'
    case 'СКФО': return ''
    case 'ПФО': return 'success'
    case 'УрФО': return 'warning'
    case 'СФО': return 'success'
    case 'ДФО': return 'info'
    case 'НЕОПР': return 'info'
    default: return 'info'
  }
}
</script>

<style scoped>
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--app-text-primary);
}

.table-count {
  font-size: 14px;
  color: var(--app-text-secondary);
  font-weight: 600;
}

.posts-table :deep(.el-table__row) {
  cursor: pointer;
}

.content-cell {
  padding: 4px 0;
}

.ai-reason {
  display: flex;
  gap: 6px;
  align-items: flex-start;
}

.ai-reason .ai-badge {
  flex-shrink: 0;
  font-size: 15px;
}

.ai-reason :deep(.markdown-body) {
  font-size: 14px;
  color: var(--app-text-regular);
  font-style: italic;
}

.ai-reason :deep(.markdown-body p) {
  margin: 0;
}

.content-preview :deep(.markdown-body) {
  font-size: 15px;
  color: var(--app-text-primary);
}

.content-preview :deep(.markdown-body p) {
  margin: 0;
}

.date-cell {
  font-size: 14px;
  color: var(--app-text-regular);
  font-weight: 600;
}

.stats-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  color: var(--app-text-secondary);
  font-weight: 600;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 6px;
}

.empty-desc {
  text-align: center;
  color: var(--app-text-secondary);
}

.empty-desc p {
  margin-bottom: 12px;
  font-size: 15px;
}
</style>
